import type {
  ContractorSummary,
  ContractorDetail,
  ContractorCreateInput,
  Form1099PrepSummary,
  ScheduleCSummary,
  StateIncomeEstimate,
  TaxDeadline,
  TaxFilingDraftSummary,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

// Tax settings
export interface TaxSettingsDTO {
  ein: string | null;
  stateOfIncorporation: string | null;
  accountingBasis: string | null;
}

export async function getTaxSettings(): Promise<TaxSettingsDTO> {
  const res = await apiClient.get<TaxSettingsDTO>(ENDPOINTS.tax.settings);
  return res.data;
}

export async function updateTaxSettings(input: {
  ein?: string | null;
  stateOfIncorporation?: string | null;
  accountingBasis?: string | null;
}): Promise<TaxSettingsDTO> {
  const res = await apiClient.patch<TaxSettingsDTO>(ENDPOINTS.tax.settings, input);
  return res.data;
}

// Contractors
export async function getContractors(): Promise<ContractorSummary[]> {
  const res = await apiClient.get<ContractorSummary[]>(ENDPOINTS.tax.contractors);
  return res.data;
}

export async function getContractor(id: string): Promise<ContractorDetail> {
  const res = await apiClient.get<ContractorDetail>(ENDPOINTS.tax.contractor(id));
  return res.data;
}

export async function createContractor(input: ContractorCreateInput): Promise<ContractorDetail> {
  const res = await apiClient.post<ContractorDetail>(ENDPOINTS.tax.contractors, input);
  return res.data;
}

export async function deleteContractor(id: string): Promise<{ deleted: boolean }> {
  const res = await apiClient.delete<{ deleted: boolean }>(ENDPOINTS.tax.contractor(id));
  return res.data;
}

// 1099 prep
export async function get1099Prep(taxYear: number): Promise<Form1099PrepSummary> {
  const res = await apiClient.get<Form1099PrepSummary>(ENDPOINTS.tax.prep1099, {
    params: { taxYear },
  });
  return res.data;
}

// Schedule C
export async function getScheduleCSummary(taxYear: number): Promise<ScheduleCSummary> {
  const res = await apiClient.get<ScheduleCSummary>(ENDPOINTS.tax.scheduleC, {
    params: { taxYear },
  });
  return res.data;
}

// State estimate
export async function getStateEstimate(taxYear?: number): Promise<StateIncomeEstimate | null> {
  const res = await apiClient.get<StateIncomeEstimate | null>(ENDPOINTS.tax.stateEstimate, {
    params: taxYear ? { taxYear } : undefined,
  });
  return res.data;
}

// Deadlines
export async function getUsDeadlines(): Promise<TaxDeadline[]> {
  const res = await apiClient.get<TaxDeadline[]>(ENDPOINTS.tax.deadlines);
  return res.data;
}

// Filing drafts
export async function getUsFilingDrafts(params?: {
  filingType?: string;
  status?: string;
}): Promise<TaxFilingDraftSummary[]> {
  const res = await apiClient.get<TaxFilingDraftSummary[]>(ENDPOINTS.tax.filingDrafts, {
    params,
  });
  return res.data;
}
